// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/spinner.tsx
import { jsx } from "react/jsx-runtime";
var sizeClasses = {
  sm: "size-3",
  md: "size-4",
  lg: "size-5"
};
function Spinner({ className, size = "md", ...props }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      role: "status",
      "aria-hidden": props["aria-hidden"] ?? true,
      className: cn(
        "inline-block animate-spin rounded-full border-2 border-current border-r-transparent",
        sizeClasses[size],
        className
      ),
      ...props
    }
  );
}

// src/components/card.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function Card({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card",
      className: cn(
        "bg-card text-card-foreground flex flex-col gap-4 rounded-3xl border border-border/60 bg-card/80 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg",
        className
      ),
      ...props
    }
  );
}
function CardHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card-header",
      className: cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 pb-4 has-data-[slot=card-action]:grid-cols-[1fr_auto]",
        className
      ),
      ...props
    }
  );
}
function CardTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card-title",
      className: cn("leading-none font-semibold", className),
      ...props
    }
  );
}
function CardDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card-description",
      className: cn("text-muted-foreground text-sm lg:text-xs", className),
      ...props
    }
  );
}
function CardAction({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card-action",
      className: cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      ),
      ...props
    }
  );
}
function CardContent({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card-content",
      className: cn("", className),
      ...props
    }
  );
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-slot": "card-footer",
      className: cn("flex items-center pt-4", className),
      ...props
    }
  );
}

// src/components/button.tsx
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { Fragment, jsx as jsx3, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 data-[loading=true]:cursor-wait data-[loading=true]:opacity-80 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline: "border border-border bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-primary/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent/80 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-lg px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Button({
  className,
  variant = "default",
  size = "default",
  isLoading = false,
  asChild = false,
  children,
  disabled,
  ...props
}) {
  const Comp = asChild ? Slot : "button";
  const resolvedDisabled = disabled || isLoading;
  const spinnerSize = size === "sm" || size === "icon-sm" ? "sm" : "md";
  const content = asChild ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
    isLoading ? /* @__PURE__ */ jsx3(Spinner, { size: spinnerSize }) : null,
    children
  ] });
  return /* @__PURE__ */ jsx3(
    Comp,
    {
      "data-slot": "button",
      "data-variant": variant,
      "data-size": size,
      "data-loading": isLoading ? "true" : void 0,
      "aria-busy": isLoading || void 0,
      className: cn(buttonVariants({ variant, size, className })),
      disabled: resolvedDisabled,
      ...props,
      children: content
    }
  );
}

// src/components/badge.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 gap-1.5",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success: "border-transparent bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50",
        warning: "border-transparent bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50",
        error: "border-transparent bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:hover:bg-rose-900/50",
        neutral: "border-transparent bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-800/80"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, icon: Icon, children, ...props }) {
  return /* @__PURE__ */ jsxs2("div", { className: cn(badgeVariants({ variant }), className), ...props, children: [
    Icon && /* @__PURE__ */ jsx4(Icon, { className: "h-3.5 w-3.5" }),
    children
  ] });
}

// src/components/input.tsx
import * as React from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var inputBaseStyles = "flex h-11 w-full rounded-md border border-border/60 bg-background px-3 py-2 text-base text-foreground shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive aria-invalid:ring-destructive/20 sm:text-sm";
var Input = React.forwardRef(
  ({ className, type, ...props }, ref) => /* @__PURE__ */ jsx5(
    "input",
    {
      ref,
      type,
      "data-slot": "input",
      className: cn(inputBaseStyles, className),
      ...props
    }
  )
);
Input.displayName = "Input";

// src/components/skeleton.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx6(
    "div",
    {
      "data-slot": "skeleton",
      className: cn("animate-pulse rounded-2xl border border-border/60 bg-muted/40", className),
      ...props
    }
  );
}

// src/components/gradient-text.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var GRADIENTS = {
  primary: "bg-[image:var(--dobbix-gradient-brand)] bg-clip-text text-transparent",
  subtle: "bg-[image:var(--dobbix-gradient-muted)] bg-clip-text text-transparent",
  warm: "bg-[image:var(--dobbix-gradient-warm)] bg-clip-text text-transparent",
  cool: "bg-[image:var(--dobbix-gradient-cool)] bg-clip-text text-transparent"
};
function GradientText({
  children,
  className,
  variant = "primary",
  animate = false
}) {
  return /* @__PURE__ */ jsx7(
    "span",
    {
      className: cn(
        GRADIENTS[variant],
        animate && "animate-gradient-x bg-[300%_300%]",
        className
      ),
      children
    }
  );
}
export {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  GradientText,
  Input,
  Skeleton,
  Spinner,
  badgeVariants,
  buttonVariants,
  cn,
  inputBaseStyles
};
//# sourceMappingURL=index.mjs.map